import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import type {
  AccessScope,
  AdminPermissions,
  AdminUserContract,
  AdminUserRole,
  AdminUserStatus,
  AuthProvider,
  DigestFrequency,
} from '@/contracts/admin-users'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import { useAuth } from '@/lib/auth'
import {
  createDraftAdminProfile,
  createInternalAdminProfile,
  listAdminUsers,
  saveAdminUserToApi,
} from '@/lib/data-client/admin-users'
import { hasFormAdminAccess, isAdminEmail, normalizePermissionEmail } from '@/lib/permissions'
import { adminFormPaneDefaults, getTwoPaneGridTemplate } from '@/lib/resizable-panes'
import { cn } from '@/lib/utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarClock,
  ClipboardList,
  Download,
  KeyRound,
  type LucideIcon,
  Mail,
  Plus,
  Save,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  Users,
} from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useId, useMemo, useState } from 'react'

type AdminStatus = AdminUserStatus
type AdminRole = AdminUserRole
type AdminProfile = AdminUserContract
type AdminFormState = AdminProfile & {
  password?: string
  removePassword?: boolean
}

type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'

const statusMeta: Record<AdminStatus, { label: string; variant: BadgeVariant; className: string }> =
  {
    active: {
      label: '啟用中',
      variant: 'outline',
      className: 'border-emerald-200 bg-emerald-100 text-emerald-800',
    },
    invited: {
      label: '已邀請',
      variant: 'outline',
      className: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    },
    suspended: {
      label: '已停用',
      variant: 'outline',
      className: 'border-slate-200 bg-slate-100 text-slate-700',
    },
    locked: {
      label: '已鎖定',
      variant: 'outline',
      className: 'border-rose-200 bg-rose-100 text-rose-700',
    },
  }

const roleMeta: Record<AdminRole, { label: string; description: string }> = {
  owner: { label: '擁有者', description: '完整工作區與管理員權限' },
  admin: { label: '管理員', description: '可管理主要後台資料與權限' },
  operator: { label: '營運管理', description: '可管理問卷、客戶與回應' },
  auditor: { label: '稽核檢視', description: '可查看紀錄與匯出前審核' },
  support: { label: '客服支援', description: '可查看客戶與回應協助處理' },
  billing: { label: '帳務管理', description: '可管理帳務與方案資訊' },
}

const permissionGroups: Array<{
  title: string
  description: string
  items: Array<{ key: keyof AdminPermissions; label: string; description: string }>
}> = [
  {
    title: '問卷與回應',
    description: '控制建立、發布、分類與回應資料處理。',
    items: [
      { key: 'manageForms', label: '管理問卷', description: '建立、編輯與封存問卷。' },
      { key: 'publishForms', label: '發布問卷', description: '上線、關閉與重新發布問卷。' },
      { key: 'manageFolders', label: '管理資料夾', description: '新增、編輯與移動問卷資料夾。' },
      { key: 'viewResponses', label: '查看回應', description: '查看客戶填答內容與回應明細。' },
      {
        key: 'exportResponses',
        label: '匯出回應',
        description: '未來匯出權限 contract；目前不產生真實 CSV、Excel 或 JSON。',
      },
    ],
  },
  {
    title: '營運與客戶',
    description: '控制客戶資訊、品牌與外部整合。',
    items: [
      {
        key: 'manageCustomers',
        label: '管理客戶',
        description: '新增、編輯與 future export contract；目前不下載真實客戶資料。',
      },
      {
        key: 'manageBranding',
        label: '管理品牌',
        description: '修改 Logo、Banner 與填答頁品牌設定。',
      },
      { key: 'manageIntegrations', label: '管理整合', description: '設定 CRM、ERP 或第三方連接。' },
      { key: 'manageWebhooks', label: '管理 Webhook', description: '建立、測試與停用 Webhook。' },
    ],
  },
  {
    title: '系統與高風險操作',
    description: '這些權限應定期審核並保留操作紀錄。',
    items: [
      { key: 'manageAdmins', label: '管理管理員', description: '邀請、停用與調整其他管理員權限。' },
      { key: 'manageBilling', label: '管理帳務', description: '查看方案、付款、發票與信用額度。' },
      {
        key: 'viewAuditLogs',
        label: '查看稽核紀錄',
        description: '查看登入、匯出與權限異動紀錄。',
      },
      { key: 'deleteData', label: '刪除資料', description: '刪除回應、客戶或敏感資料。' },
      { key: 'impersonateUsers', label: '代入使用者', description: '用於支援情境，需高階審核。' },
    ],
  },
]

const adminsFixture: AdminProfile[] = [createInternalAdminProfile()]

export default function AdminsRoute() {
  const { user, isLoading } = useAuth()
  const queryClient = useQueryClient()
  const adminUsersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: listAdminUsers,
    enabled: !isLoading,
    staleTime: 30_000,
  })
  const [admins, setAdmins] = useState<AdminProfile[]>(adminsFixture)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [editingAdmin, setEditingAdmin] = useState<AdminFormState | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogTab, setDialogTab] = useState('profile')
  const [formError, setFormError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (adminUsersQuery.data?.data) setAdmins(adminUsersQuery.data.data)
  }, [adminUsersQuery.data])

  const adminEmails = useMemo(() => admins.map((admin) => admin.email), [admins])
  const canManageAdmins = hasFormAdminAccess(user, adminEmails)
  const currentEmailIsAdmin = isAdminEmail(user?.email, adminEmails)

  const filteredAdmins = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    return admins.filter((admin) => {
      const matchesKeyword =
        !normalizedKeyword ||
        [
          admin.email,
          admin.displayName,
          admin.legalName,
          admin.jobTitle,
          admin.department,
          admin.employeeId,
        ]
          .join(' ')
          .toLocaleLowerCase()
          .includes(normalizedKeyword)
      const matchesStatus = statusFilter === 'all' || admin.status === statusFilter
      const matchesRole = roleFilter === 'all' || admin.role === roleFilter
      return matchesKeyword && matchesStatus && matchesRole
    })
  }, [admins, keyword, roleFilter, statusFilter])

  const stats = useMemo(() => {
    const activeCount = admins.filter((admin) => admin.status === 'active').length
    const emailAllowCount = admins.filter((admin) => normalizePermissionEmail(admin.email)).length
    const highRiskCount = admins.filter(
      (admin) => admin.riskLevel === 'high' || admin.permissions.deleteData,
    ).length
    const mfaCoverage = admins.length
      ? Math.round((admins.filter((admin) => admin.mfaEnabled).length / admins.length) * 100)
      : 0
    return { activeCount, emailAllowCount, highRiskCount, mfaCoverage }
  }, [admins])

  function openEditDialog(admin: AdminProfile, tab = 'profile') {
    setEditingAdmin({ ...structuredClone(admin), password: '', removePassword: false })
    setDialogTab(tab)
    setFormError(null)
    setDialogOpen(true)
  }

  function openCreateDialog() {
    setEditingAdmin({
      ...createDraftAdminProfile(),
      password: '',
      removePassword: false,
      passwordConfigured: false,
    })
    setDialogTab('profile')
    setFormError(null)
    setDialogOpen(true)
  }

  function updateEditingAdmin(patch: Partial<AdminFormState>) {
    setEditingAdmin((current) => (current ? { ...current, ...patch } : current))
    setFormError(null)
  }

  function updatePermission(key: keyof AdminPermissions, value: boolean) {
    setEditingAdmin((current) =>
      current
        ? {
            ...current,
            permissions: {
              ...current.permissions,
              [key]: value,
            },
          }
        : current,
    )
    setFormError(null)
  }

  async function saveAdmin() {
    if (!editingAdmin) return
    const email = normalizePermissionEmail(editingAdmin.email)
    if (!email) {
      setFormError('請輸入管理員 Email。')
      setDialogTab('profile')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Email 格式不正確。')
      setDialogTab('profile')
      return
    }
    const duplicated = admins.some(
      (admin) => admin.id !== editingAdmin.id && normalizePermissionEmail(admin.email) === email,
    )
    if (duplicated) {
      setFormError('此 Email 已在管理員清單中。')
      setDialogTab('profile')
      return
    }

    const password = editingAdmin.password?.trim()
    const nextAdmin = {
      ...editingAdmin,
      email,
      password: password || undefined,
      removePassword: editingAdmin.removePassword ? true : undefined,
      updatedAt: new Date().toISOString().slice(0, 10),
    }
    try {
      setIsSaving(true)
      const saved = await saveAdminUserToApi(nextAdmin)
      setAdmins((current) => {
        const exists = current.some((admin) => admin.id === saved.id)
        return exists
          ? current.map((admin) => (admin.id === saved.id ? saved : admin))
          : [saved, ...current]
      })
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setDialogOpen(false)
      setEditingAdmin(null)
      setFormError(null)
    } catch (error) {
      setFormError(error instanceof Error ? error.message : '儲存管理員失敗。')
    } finally {
      setIsSaving(false)
    }
  }

  async function suspendAdmin(adminId: string) {
    const target = admins.find((admin) => admin.id === adminId)
    if (!target || isProtectedInternalAdmin(target)) return
    const nextAdmin = {
      ...target,
      status: target.status === 'suspended' ? 'active' : ('suspended' as AdminStatus),
      updatedAt: new Date().toISOString().slice(0, 10),
    }
    try {
      const saved = await saveAdminUserToApi(nextAdmin)
      setAdmins((current) => current.map((admin) => (admin.id === saved.id ? saved : admin)))
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    } catch (error) {
      setFormError(error instanceof Error ? error.message : '更新管理員狀態失敗。')
    }
  }

  if (isLoading || adminUsersQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4 lg:p-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-[620px] w-full" />
      </div>
    )
  }

  if (!canManageAdmins) {
    return (
      <div className="flex min-h-[70dvh] items-center justify-center p-4 lg:p-6">
        <Card className="max-w-xl">
          <CardHeader>
            <div className="mb-2 grid size-11 place-items-center rounded-lg bg-amber-100 text-amber-700">
              <ShieldCheck className="size-5" />
            </div>
            <CardTitle>沒有管理員權限</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <p>
              此頁會依登入 Email 與管理員清單判斷權限。你的 Email 尚未列入管理員清單，也不是
              owner/admin 角色。
            </p>
            <p className="rounded-md border border-slate-200 bg-slate-50 p-3">
              請聯絡現有管理員將你的 Email 加入清單，或由工作區擁有者調整角色。
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 p-4 lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">管理員</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={openCreateDialog}>
            <Plus className="size-4" />
            新增管理員
          </Button>
          <Button
            variant="outline"
            disabled
            title="匯出清單目前只保留 contract preview；未接後端 export job 前不產生真實 CSV。"
          >
            <Download className="size-4" />
            匯出未接線
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={Users}
          label="啟用管理員"
          value={`${stats.activeCount}`}
          detail="狀態為啟用中的帳號"
        />
        <MetricCard
          icon={Mail}
          label="Email allowlist"
          value={`${stats.emailAllowCount}`}
          detail={currentEmailIsAdmin ? '目前登入 Email 已命中清單' : '目前登入 Email 未命中清單'}
        />
        <MetricCard
          icon={AlertTriangle}
          label="高風險權限"
          value={`${stats.highRiskCount}`}
          detail="含刪除資料或高風險帳號"
        />
        <MetricCard
          icon={KeyRound}
          label="MFA 覆蓋率"
          value={`${stats.mfaCoverage}%`}
          detail="已啟用 MFA 的管理員比例"
        />
      </div>
      {adminUsersQuery.isError ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          管理員清單讀取失敗：
          {adminUsersQuery.error instanceof Error ? adminUsersQuery.error.message : '未知錯誤'}
        </div>
      ) : null}

      <Card>
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <CardTitle>管理員清單</CardTitle>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative min-w-72">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={keyword}
                  onChange={(event) => setKeyword(event.currentTarget.value)}
                  placeholder="搜尋 Email、姓名、部門、員編"
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部狀態</SelectItem>
                  <SelectItem value="active">啟用中</SelectItem>
                  <SelectItem value="invited">已邀請</SelectItem>
                  <SelectItem value="suspended">已停用</SelectItem>
                  <SelectItem value="locked">已鎖定</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部角色</SelectItem>
                  {Object.entries(roleMeta).map(([role, meta]) => (
                    <SelectItem key={role} value={role}>
                      {meta.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>管理員</TableHead>
                <TableHead>Email 權限</TableHead>
                <TableHead>角色</TableHead>
                <TableHead>狀態</TableHead>
                <TableHead>MFA</TableHead>
                <TableHead>最近登入</TableHead>
                <TableHead>審核</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="grid size-10 place-items-center rounded-full bg-indigo-100 font-semibold text-indigo-700">
                        {admin.displayName.slice(0, 1) || 'A'}
                      </div>
                      <div>
                        <div className="font-medium text-slate-950">
                          {admin.displayName || '未命名管理員'}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {admin.department || '未設定部門'} · {admin.jobTitle || '未設定職稱'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{admin.email || '尚未設定 Email'}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {isAdminEmail(admin.email, adminEmails)
                        ? '登入 Email 命中管理員清單'
                        : '未命中'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{roleMeta[admin.role].label}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={statusMeta[admin.status].variant}
                      className={statusMeta[admin.status].className}
                    >
                      {statusMeta[admin.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={admin.mfaEnabled ? 'success' : 'warning'}>
                      {admin.mfaEnabled ? '已啟用' : '待設定'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {admin.lastLoginAt || '尚未登入'}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {admin.lastPermissionReviewAt || '待審核'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(admin)}>
                        編輯
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isProtectedInternalAdmin(admin)}
                        className={cn(
                          admin.status === 'suspended'
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                        )}
                        onClick={() => suspendAdmin(admin.id)}
                      >
                        {admin.status === 'suspended' ? '啟用' : '停用'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredAdmins.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              找不到符合條件的管理員。
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-4 p-5 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <div className="flex items-center gap-2 font-semibold">
              <ShieldCheck className="size-4 text-indigo-600" />
              Email 權限判斷方式
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              用戶登入後，系統會先看 session 角色是否為 owner/admin，再比對登入 Email
              是否在管理員清單中。命中任一條件即可取得管理權限。
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {admins.map((admin) => (
              <div
                key={admin.id}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <div className="truncate text-sm font-medium">
                  {normalizePermissionEmail(admin.email)}
                </div>
                <div className="mt-1 text-xs text-slate-500">{roleMeta[admin.role].label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AdminFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) {
            setEditingAdmin(null)
            setFormError(null)
          }
        }}
        admin={editingAdmin}
        activeTab={dialogTab}
        onActiveTabChange={setDialogTab}
        error={formError}
        onChange={updateEditingAdmin}
        onPermissionChange={updatePermission}
        onSave={saveAdmin}
        isSaving={isSaving}
      />
    </div>
  )
}

function isProtectedInternalAdmin(admin: AdminProfile) {
  return (
    admin.id === 'internal-admin' ||
    normalizePermissionEmail(admin.email) === 'admin@miao-form.case4.app'
  )
}

function AdminFormDialog({
  open,
  onOpenChange,
  admin,
  activeTab,
  onActiveTabChange,
  error,
  onChange,
  onPermissionChange,
  onSave,
  isSaving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  admin: AdminFormState | null
  activeTab: string
  onActiveTabChange: (tab: string) => void
  error: string | null
  onChange: (patch: Partial<AdminFormState>) => void
  onPermissionChange: (key: keyof AdminPermissions, value: boolean) => void
  onSave: () => void
  isSaving: boolean
}) {
  const {
    width: navWidth,
    collapsed: navCollapsed,
    startResize: startNavResize,
    toggleCollapsed: toggleNavCollapsed,
  } = useResizablePane({
    initialWidth: 240,
    config: adminFormPaneDefaults.nav,
  })
  const adminFormGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
    navConfig: adminFormPaneDefaults.nav,
  })

  if (!admin) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(86dvh,760px)] w-[min(96vw,1080px)] max-w-none flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-slate-200 px-6 py-5">
          <DialogHeader>
            <DialogTitle>管理員資料與權限</DialogTitle>
            <DialogDescription className="sr-only">
              編輯管理員資料、權限、安全、範圍、通知與審核紀錄。
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={onActiveTabChange}
          data-testid="admin-form-tabs"
          className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] overflow-hidden transition-[grid-template-columns] duration-200 ease-out lg:grid-rows-none lg:grid-cols-[var(--admin-form-grid-columns)]"
          style={{ '--admin-form-grid-columns': adminFormGridColumns } as CSSProperties}
        >
          <div className="relative min-h-0 min-w-0 overflow-hidden lg:h-full">
            <aside
              className={cn(
                'min-h-0 border-b border-slate-200 bg-slate-50 transition-opacity duration-150 lg:h-full lg:border-r lg:border-b-0',
                navCollapsed && 'pointer-events-none opacity-0',
              )}
              aria-hidden={navCollapsed}
            >
              <div
                className="h-40 overflow-x-hidden overflow-y-auto p-3 [scrollbar-gutter:stable] lg:h-full"
                data-testid="admin-form-nav-scroll"
              >
                <TabsList className="grid h-auto w-full gap-1 bg-transparent p-0">
                  <TabsTrigger value="profile">帳戶資訊</TabsTrigger>
                  <TabsTrigger value="password">密碼設定</TabsTrigger>
                  <TabsTrigger value="permissions">權限設定</TabsTrigger>
                  <TabsTrigger value="security">登入安全</TabsTrigger>
                  <TabsTrigger value="scope">資料範圍</TabsTrigger>
                  <TabsTrigger value="notifications">通知</TabsTrigger>
                  <TabsTrigger value="audit">審核紀錄</TabsTrigger>
                </TabsList>
              </div>
            </aside>
            <PaneResizeHandle
              edge="right"
              collapsed={navCollapsed}
              resizeLabel="拖曳調整管理員設定左欄寬度"
              collapseLabel="收合管理員設定左欄"
              expandLabel="展開管理員設定左欄"
              onResizeStart={startNavResize}
              onToggle={toggleNavCollapsed}
            />
          </div>

          <div
            className="min-h-0 overflow-x-hidden overflow-y-auto px-6 py-5 [scrollbar-gutter:stable]"
            data-testid="admin-form-content-scroll"
          >
            <TabsContent value="profile" className="mt-0">
              <FormSection title="帳戶資訊" icon={UserRound}>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <TextField
                    label="Email"
                    type="email"
                    value={admin.email}
                    onChange={(email) => onChange({ email })}
                    required
                  />
                  <TextField
                    label="顯示名稱"
                    value={admin.displayName}
                    onChange={(displayName) => onChange({ displayName })}
                  />
                  <TextField
                    label="法定姓名"
                    value={admin.legalName}
                    onChange={(legalName) => onChange({ legalName })}
                  />
                  <TextField
                    label="職稱"
                    value={admin.jobTitle}
                    onChange={(jobTitle) => onChange({ jobTitle })}
                  />
                  <TextField
                    label="部門"
                    value={admin.department}
                    onChange={(department) => onChange({ department })}
                  />
                  <TextField
                    label="員工編號"
                    value={admin.employeeId}
                    onChange={(employeeId) => onChange({ employeeId })}
                  />
                  <TextField
                    label="公司 / 工作區"
                    value={admin.company}
                    onChange={(company) => onChange({ company })}
                  />
                  <TextField
                    label="電話"
                    value={admin.phone}
                    onChange={(phone) => onChange({ phone })}
                  />
                  <TextField
                    label="備援 Email"
                    type="email"
                    value={admin.backupEmail}
                    onChange={(backupEmail) => onChange({ backupEmail })}
                  />
                  <SelectField
                    label="狀態"
                    value={admin.status}
                    options={[
                      { value: 'active', label: '啟用中' },
                      { value: 'invited', label: '已邀請' },
                      { value: 'suspended', label: '已停用' },
                      { value: 'locked', label: '已鎖定' },
                    ]}
                    onChange={(status) => onChange({ status: status as AdminStatus })}
                  />
                  <SelectField
                    label="角色"
                    value={admin.role}
                    options={Object.entries(roleMeta).map(([value, meta]) => ({
                      value,
                      label: meta.label,
                    }))}
                    onChange={(role) => onChange({ role: role as AdminRole })}
                  />
                  <SelectField
                    label="權限範圍"
                    value={admin.accessScope}
                    options={[
                      { value: 'workspace', label: '整個工作區' },
                      { value: 'forms', label: '問卷管理' },
                      { value: 'customers', label: '客戶管理' },
                      { value: 'analytics', label: '分析與稽核' },
                      { value: 'billing', label: '帳務管理' },
                    ]}
                    onChange={(accessScope) =>
                      onChange({ accessScope: accessScope as AccessScope })
                    }
                  />
                  <TextField
                    label="工作區角色名稱"
                    value={admin.workspaceRole}
                    onChange={(workspaceRole) => onChange({ workspaceRole })}
                  />
                  <TextField
                    label="直屬主管"
                    value={admin.managerName}
                    onChange={(managerName) => onChange({ managerName })}
                  />
                  <TextField
                    label="主管 Email"
                    type="email"
                    value={admin.managerEmail}
                    onChange={(managerEmail) => onChange({ managerEmail })}
                  />
                </div>
              </FormSection>
            </TabsContent>

            <TabsContent value="password" className="mt-0">
              <AccountPasswordSection
                passwordConfigured={admin.passwordConfigured}
                password={admin.password}
                removePassword={admin.removePassword}
                onChange={onChange}
              />
            </TabsContent>

            <TabsContent value="permissions" className="mt-0">
              <div className="space-y-4">
                {permissionGroups.map((group) => (
                  <FormSection key={group.title} title={group.title} icon={SlidersHorizontal}>
                    <p className="text-sm text-slate-500">{group.description}</p>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {group.items.map((permission) => (
                        <SwitchCard
                          key={permission.key}
                          label={permission.label}
                          description={permission.description}
                          checked={admin.permissions[permission.key]}
                          danger={
                            permission.key === 'deleteData' || permission.key === 'impersonateUsers'
                          }
                          onChange={(checked) => onPermissionChange(permission.key, checked)}
                        />
                      ))}
                    </div>
                  </FormSection>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="security" className="mt-0">
              <FormSection title="登入安全與 Email 判斷" icon={KeyRound}>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <SelectField
                    label="登入方式"
                    value={admin.authProvider}
                    options={[
                      { value: 'google', label: 'Google OAuth' },
                      { value: 'email_magic_link', label: 'Email Magic Link' },
                      { value: 'sso', label: 'SSO' },
                      { value: 'manual_invite', label: '手動邀請' },
                    ]}
                    onChange={(authProvider) =>
                      onChange({ authProvider: authProvider as AuthProvider })
                    }
                  />
                  <TextField
                    label="SSO Subject"
                    value={admin.ssoSubject}
                    onChange={(ssoSubject) => onChange({ ssoSubject })}
                  />
                  <NumberField
                    label="Session timeout 分鐘"
                    value={admin.sessionTimeoutMinutes}
                    min={15}
                    max={1440}
                    onChange={(sessionTimeoutMinutes) => onChange({ sessionTimeoutMinutes })}
                  />
                  <TextField
                    label="時區"
                    value={admin.timezone}
                    onChange={(timezone) => onChange({ timezone })}
                  />
                  <TextField
                    label="語言"
                    value={admin.language}
                    onChange={(language) => onChange({ language })}
                  />
                  <TextField
                    label="國家"
                    value={admin.country}
                    onChange={(country) => onChange({ country })}
                  />
                  <TextField
                    label="城市"
                    value={admin.city}
                    onChange={(city) => onChange({ city })}
                  />
                  <TextField
                    label="最近登入時間"
                    value={admin.lastLoginAt}
                    onChange={(lastLoginAt) => onChange({ lastLoginAt })}
                  />
                  <TextField
                    label="最近 IP"
                    value={admin.lastIp}
                    onChange={(lastIp) => onChange({ lastIp })}
                  />
                  <TextField
                    label="最近 User Agent"
                    value={admin.lastUserAgent}
                    onChange={(lastUserAgent) => onChange({ lastUserAgent })}
                  />
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <SwitchCard
                    label="Email 已驗證"
                    description="未驗證時不應開放高權限。"
                    checked={admin.loginEmailVerified}
                    onChange={(loginEmailVerified) => onChange({ loginEmailVerified })}
                  />
                  <SwitchCard
                    label="允許 Email 登入"
                    description="允許 magic link 或 OTP 登入。"
                    checked={admin.allowEmailLogin}
                    onChange={(allowEmailLogin) => onChange({ allowEmailLogin })}
                  />
                  <SwitchCard
                    label="允許 Google 登入"
                    description="允許 Google OAuth 登入。"
                    checked={admin.allowGoogleLogin}
                    onChange={(allowGoogleLogin) => onChange({ allowGoogleLogin })}
                  />
                  <SwitchCard
                    label="必須 MFA"
                    description="所有管理員建議啟用。"
                    checked={admin.mfaRequired}
                    onChange={(mfaRequired) => onChange({ mfaRequired })}
                  />
                  <SwitchCard
                    label="已啟用 MFA"
                    description="顯示目前 MFA 狀態。"
                    checked={admin.mfaEnabled}
                    onChange={(mfaEnabled) => onChange({ mfaEnabled })}
                  />
                  <SwitchCard
                    label="要求可信裝置"
                    description="未信任裝置需額外驗證。"
                    checked={admin.deviceTrustRequired}
                    onChange={(deviceTrustRequired) => onChange({ deviceTrustRequired })}
                  />
                  <SwitchCard
                    label="要求重設密碼"
                    description="下次登入前需更新登入方式。"
                    checked={admin.passwordResetRequired}
                    onChange={(passwordResetRequired) => onChange({ passwordResetRequired })}
                  />
                  <SwitchCard
                    label="匯出需審批"
                    description="降低大量資料外流風險。"
                    checked={admin.dataExportApprovalRequired}
                    onChange={(dataExportApprovalRequired) =>
                      onChange({ dataExportApprovalRequired })
                    }
                  />
                </div>
                <TextareaField
                  label="IP Allowlist"
                  value={admin.ipAllowlist}
                  onChange={(ipAllowlist) => onChange({ ipAllowlist })}
                  hint="可輸入多個 CIDR 或 IP，以逗號或換行分隔。"
                />
                <TextareaField
                  label="允許 Email 網域"
                  value={admin.allowedDomains}
                  onChange={(allowedDomains) => onChange({ allowedDomains })}
                  hint="例如：miao-form.case4.app, example.com"
                />
              </FormSection>
            </TabsContent>

            <TabsContent value="scope" className="mt-0">
              <FormSection title="資料範圍與預設工作區" icon={ClipboardList}>
                <div className="grid gap-4 md:grid-cols-2">
                  <TextareaField
                    label="問卷範圍"
                    value={admin.formScope}
                    onChange={(formScope) => onChange({ formScope })}
                  />
                  <TextareaField
                    label="客戶範圍"
                    value={admin.customerScope}
                    onChange={(customerScope) => onChange({ customerScope })}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <TextField
                    label="預設登入頁"
                    value={admin.defaultLandingPage}
                    onChange={(defaultLandingPage) => onChange({ defaultLandingPage })}
                  />
                  <TextField
                    label="邀請日期"
                    type="date"
                    value={admin.invitedAt}
                    onChange={(invitedAt) => onChange({ invitedAt })}
                  />
                  <TextField
                    label="邀請到期日"
                    type="date"
                    value={admin.invitationExpiresAt}
                    onChange={(invitationExpiresAt) => onChange({ invitationExpiresAt })}
                  />
                  <TextField
                    label="建立日期"
                    type="date"
                    value={admin.createdAt}
                    onChange={(createdAt) => onChange({ createdAt })}
                  />
                </div>
              </FormSection>
            </TabsContent>

            <TabsContent value="notifications" className="mt-0">
              <FormSection title="通知偏好" icon={Mail}>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <SwitchCard
                    label="安全通知"
                    description="登入異常、MFA、權限異動。"
                    checked={admin.notifySecurity}
                    onChange={(notifySecurity) => onChange({ notifySecurity })}
                  />
                  <SwitchCard
                    label="匯出通知"
                    description="回應與客戶資料匯出。"
                    checked={admin.notifyExports}
                    onChange={(notifyExports) => onChange({ notifyExports })}
                  />
                  <SwitchCard
                    label="帳務通知"
                    description="付款、發票、方案異動。"
                    checked={admin.notifyBilling}
                    onChange={(notifyBilling) => onChange({ notifyBilling })}
                  />
                  <SwitchCard
                    label="新回應通知"
                    description="問卷收到新填答時通知。"
                    checked={admin.notifyNewResponses}
                    onChange={(notifyNewResponses) => onChange({ notifyNewResponses })}
                  />
                </div>
                <SelectField
                  label="摘要頻率"
                  value={admin.digestFrequency}
                  options={[
                    { value: 'instant', label: '即時' },
                    { value: 'daily', label: '每日' },
                    { value: 'weekly', label: '每週' },
                    { value: 'off', label: '關閉' },
                  ]}
                  onChange={(digestFrequency) =>
                    onChange({ digestFrequency: digestFrequency as DigestFrequency })
                  }
                />
              </FormSection>
            </TabsContent>

            <TabsContent value="audit" className="mt-0">
              <FormSection title="審核與內部紀錄" icon={CalendarClock}>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <TextField
                    label="最後權限審核日"
                    type="date"
                    value={admin.lastPermissionReviewAt}
                    onChange={(lastPermissionReviewAt) => onChange({ lastPermissionReviewAt })}
                  />
                  <TextField
                    label="審核人"
                    value={admin.reviewedBy}
                    onChange={(reviewedBy) => onChange({ reviewedBy })}
                  />
                  <SelectField
                    label="風險等級"
                    value={admin.riskLevel}
                    options={[
                      { value: 'low', label: '低' },
                      { value: 'medium', label: '中' },
                      { value: 'high', label: '高' },
                    ]}
                    onChange={(riskLevel) =>
                      onChange({ riskLevel: riskLevel as AdminProfile['riskLevel'] })
                    }
                  />
                </div>
                <TextareaField
                  label="稽核備註"
                  value={admin.auditNotes}
                  onChange={(auditNotes) => onChange({ auditNotes })}
                />
                <TextareaField
                  label="內部備註"
                  value={admin.internalNotes}
                  onChange={(internalNotes) => onChange({ internalNotes })}
                />
              </FormSection>
            </TabsContent>
          </div>
        </Tabs>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button onClick={onSave} disabled={isSaving}>
              <Save className="size-4" />
              {isSaving ? '儲存中...' : '儲存管理員'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function MetricCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: LucideIcon
  label: string
  value: string
  detail: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-5">
        <div className="grid size-11 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
          <Icon className="size-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-slate-500">{label}</div>
          <div className="mt-1 truncate text-2xl font-semibold">{value}</div>
          <div className="mt-1 text-xs text-slate-500">{detail}</div>
        </div>
      </CardContent>
    </Card>
  )
}

function FormSection({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: LucideIcon
  children: ReactNode
}) {
  return (
    <div className="space-y-5 rounded-md border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center gap-2">
        <div className="grid size-8 place-items-center rounded-md bg-white text-indigo-700 shadow-sm">
          <Icon className="size-4" />
        </div>
        <h2 className="font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  )
}

function AccountPasswordSection({
  passwordConfigured,
  password,
  removePassword,
  onChange,
}: {
  passwordConfigured?: boolean
  password?: string
  removePassword?: boolean
  onChange: (patch: Partial<AdminFormState>) => void
}) {
  const passwordId = useId()
  const hasPendingPassword = Boolean(password?.trim())
  const statusLabel = removePassword
    ? '將移除密碼'
    : hasPendingPassword || passwordConfigured
      ? '已設定密碼'
      : '未設定密碼'
  const statusClass = removePassword
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : hasPendingPassword || passwordConfigured
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-100 text-slate-600'

  return (
    <FormSection title="密碼設定" icon={KeyRound}>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="w-full max-w-md space-y-2">
          <Label htmlFor={passwordId}>新密碼</Label>
          <Input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            value={password ?? ''}
            onChange={(event) => {
              const nextPassword = event.currentTarget.value
              onChange({
                password: nextPassword,
                removePassword: false,
              })
            }}
          />
        </div>
        <Badge variant="outline" className={cn('w-fit', statusClass)}>
          {statusLabel}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!passwordConfigured && !hasPendingPassword}
          onClick={() =>
            onChange({
              password: '',
              removePassword: true,
              passwordConfigured: false,
            })
          }
        >
          移除密碼
        </Button>
        {removePassword ? (
          <Button type="button" variant="ghost" onClick={() => onChange({ removePassword: false })}>
            取消移除
          </Button>
        ) : null}
      </div>
    </FormSection>
  )
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  required,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: 'text' | 'email' | 'date'
  required?: boolean
}) {
  const id = useId()

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {required ? <span className="ml-1 text-rose-600">*</span> : null}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </div>
  )
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
}) {
  const id = useId()

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value) || 0)}
      />
    </div>
  )
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  const id = useId()

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function TextareaField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
}) {
  const id = useId()

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} value={value} onChange={(event) => onChange(event.currentTarget.value)} />
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  )
}

function SwitchCard({
  label,
  description,
  checked,
  danger,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  danger?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div
      className={cn(
        'flex min-h-24 items-start justify-between gap-4 rounded-md border border-slate-200 bg-white p-4',
        danger && checked && 'border-rose-200 bg-rose-50',
      )}
    >
      <div>
        <div className="font-medium text-slate-950">{label}</div>
        <p className="mt-1 text-sm leading-5 text-slate-500">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}
